/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return */
import {
  BaseCard,
  DocumentSections,
  DropzoneFileItem,
  FileCollectionMap as RfpDocumentResponse,
} from "@platform/design-system";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApi } from "@platform/api";
import { useSignedInUser } from "@platform/user/client";

import {
  CARRIER_PROPOSAL_ACCEPTED_FILE_FORMATS,
  CARRIER_PROPOSAL_ACCEPTED_FILE_FORMATS_LABEL,
  MARKETING_ACCEPTED_FILE_FORMATS,
  MARKETING_ACCEPTED_FILE_FORMATS_LABEL,
  MAX_MB_RFP_FILE_SIZE,
  MAX_MB_RFP_PROPOSAL_FILE_SIZE,
} from "../../../constants";
import { useRfpDocMutation, useToastNotificationService } from "../../../hooks";
import { downloadBinaryFile, extractErrorMessage } from "../../../utils";

type DownloadVariables = {
  downloadLink: string;
  section: string;
};

type Props = {
  rfpId: string;
  displayInCard?: boolean;
  onMarketingPresentationUploaded?: VoidFunction;
};

const RfpDocumentsCard = ({
  rfpId,
  displayInCard = true,
  onMarketingPresentationUploaded,
}: Props): React.JSX.Element => {
  const axiosInstance = useApi();
  const { isBrokerOrAgent } = useSignedInUser();
  const { createToastNotification } = useToastNotificationService();
  const rfpDocuemtnsQueryKey = ["rfp", "documents", rfpId];
  const query = useQuery<RfpDocumentResponse, Error>({
    queryKey: rfpDocuemtnsQueryKey,
    queryFn: () =>
      axiosInstance
        .get(`brokerportal/v1/rfp/${rfpId}/broker-documents`, {})
        .then((res) => res.data),
  });
  const {
    loading: loadingMarketing,
    onDelete: onDeleteMarketing,
    onUpload: onUploadMarketing,
  } = useRfpDocMutation(
    {
      rfpId,
      uploadEndpoint: "brokerportal/v1/uploadMarketingPresentation",
      createUploadFormData: (files) => {
        const formData = new FormData();
        formData.append("rfpId", rfpId);
        formData.append("marketingPresentation", files[0]);
        return formData;
      },
      getOptimisticUpload: (oldResponse, files) => ({
        ...oldResponse,
        marketingPresentations: [files[0]],
      }),
      getOptimisticDelete: (oldResponse, _) => ({
        ...oldResponse,
        marketingPresentations: [],
      }),
      deleteEndpoint: "brokerportal/v1/deleteMarketingPresentation",
      onUploadSuccess: onMarketingPresentationUploaded,
      errorMessages: {
        deleteError: "Error deleting marketing presentation!",
        uploadError: "Unexpected error occurred. Try again later!",
      },
    },
    rfpDocuemtnsQueryKey,
  );
  const {
    loading: loadingProposal,
    onDelete: onDeleteProposal,
    onUpload: onUploadProposal,
  } = useRfpDocMutation(
    {
      rfpId,
      uploadEndpoint: "brokerportal/v1/uploadCarrierProposal",
      createUploadFormData: (files) => {
        const formData = new FormData();
        formData.append("rfp", rfpId);
        files.forEach((file) => {
          formData.append(file.name, file);
        });
        return formData;
      },
      getOptimisticUpload: (oldResponse, files) => ({
        ...oldResponse,
        proposals: [...(oldResponse.proposals ?? []), ...files],
      }),
      getOptimisticDelete: (oldResponse, fileName) => ({
        ...oldResponse,
        proposals: oldResponse.proposals.filter(
          (file) => file.name !== fileName,
        ),
      }),
      deleteEndpoint: "brokerportal/v1/deleteRfpProposal",
      errorMessages: {
        deleteError: "Error deleting proposal!",
        uploadError: "Unexpected error occurred. Try again later!",
      },
    },
    rfpDocuemtnsQueryKey,
  );

  const downloadMutation = useMutation<Blob, Error, DownloadVariables>({
    mutationFn: ({ downloadLink }) =>
      axiosInstance
        .post(
          "brokerportal/v1/getRfpFile",
          {
            downloadLink,
          },
          { responseType: "blob" },
        )
        .then((res) => res.data),
    onError: (error, variables) => {
      const documentType =
        variables.section === "marketingPresentations"
          ? "marketing presentation"
          : "proposal";
      createToastNotification({
        message: extractErrorMessage(
          error,
          `Error downloading ${documentType}!`,
        ),
        severity: "error",
      });
    },
  });

  const onDownloadFile = async (
    file: DropzoneFileItem,
    section: string,
  ): Promise<void> => {
    const data = await downloadMutation.mutateAsync({
      downloadLink: file.downloadLink,
      section,
    });
    downloadBinaryFile(data, file.name);
  };

  const documentSections = (
    <DocumentSections
      sections={[
        {
          title: "Marketing Presentation",
          pathToData: "marketingPresentations",
          emptyMessage: isBrokerOrAgent
            ? "Pending marketing results from carriers"
            : "",
          dropzoneProps: {
            isAllowedToManage: !isBrokerOrAgent,
            maxMbSize: MAX_MB_RFP_FILE_SIZE,
            acceptedFormatFiles: MARKETING_ACCEPTED_FILE_FORMATS,
            allowedFormatLabel: MARKETING_ACCEPTED_FILE_FORMATS_LABEL,
            handleUploadFiles: onUploadMarketing,
            onFileDelete: onDeleteMarketing,
            onFileDownload: (file) =>
              onDownloadFile(file, "marketingPresentations"),
            downloadDisabled: downloadMutation.isPending,
            isLoading:
              query.isLoading ||
              loadingMarketing ||
              (downloadMutation.isPending &&
                downloadMutation.variables?.section ===
                  "marketingPresentations"),
          },
        },
        {
          title: "Carrier Proposals",
          pathToData: "proposals",
          emptyMessage: isBrokerOrAgent ? "No Proposals added" : "",
          dropzoneProps: {
            isAllowedToManage: !isBrokerOrAgent,
            maxMbSize: MAX_MB_RFP_PROPOSAL_FILE_SIZE,
            acceptedFormatFiles: CARRIER_PROPOSAL_ACCEPTED_FILE_FORMATS,
            allowedFormatLabel: CARRIER_PROPOSAL_ACCEPTED_FILE_FORMATS_LABEL,
            isMultiple: true,
            handleUploadFiles: onUploadProposal,
            onFileDelete: onDeleteProposal,
            onFileDownload: (file) => onDownloadFile(file, "proposals"),
            downloadDisabled: downloadMutation.isPending,
            isLoading:
              query.isLoading ||
              loadingProposal ||
              (downloadMutation.isPending &&
                downloadMutation.variables?.section === "proposals"),
          },
        },
      ]}
      data={query.data}
    />
  );

  if (displayInCard) {
    return (
      <BaseCard
        data-testid="ds-card-documents"
        title={
          isBrokerOrAgent
            ? "Centro Marketing Documents"
            : "Broker Delivery Documents"
        }
      >
        {documentSections}
      </BaseCard>
    );
  }

  return documentSections;
};

export default RfpDocumentsCard;
